import { useEffect, useRef, useState } from 'react';

/**
 * The free preview: the episode's shots as a slideshow, watermarked.
 *
 * Rendered here rather than transcoded. The first attempt asked MediaConvert to
 * hold each still for a couple of seconds and produced a 2-frame, 0.08-second
 * file — a single-frame input has no duration to clip. Doing it client-side is
 * not the consolation prize: it starts instantly, needs no job to poll and no
 * second copy of the bytes, and the pan and the watermark are free.
 *
 * What it deliberately does NOT claim to be is the finished film. It shows the
 * right character in the right places; it cannot show motion, which is where
 * generated video most often goes wrong. The caption says so, because a parent
 * approving this is approving the plan.
 */
export function PreviewFilm({
  stills,
  secondsPerStill,
  onDone,
}: {
  stills: string[];
  secondsPerStill: number;
  onDone?: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!playing || stills.length === 0) return;
    timer.current = window.setTimeout(() => {
      if (index + 1 >= stills.length) {
        setPlaying(false);
        onDone?.();
      } else {
        setIndex(index + 1);
      }
    }, secondsPerStill * 1000);
    return () => window.clearTimeout(timer.current);
  }, [index, playing, stills.length, secondsPerStill, onDone]);

  if (stills.length === 0) return null;

  return (
    <div className="film">
      <div className="film-stage">
        {stills.map((url, i) => (
          // Every still stays mounted and cross-fades. Swapping the src would
          // show a blank frame while the next image loads, which reads as a
          // stutter in something meant to look like a film.
          <img
            key={url}
            src={url}
            alt=""
            className={`film-frame${i === index ? ' on' : ''}`}
            style={{ animationDuration: `${secondsPerStill * 2}s` }}
          />
        ))}
        <img
          className="film-mark"
          src="/brand/preview-watermark.png"
          alt=""
          aria-hidden="true"
        />
        {!playing && stills.length > 1 && (
          <button
            className="film-replay"
            onClick={() => {
              setIndex(0);
              setPlaying(true);
            }}
          >
            ↻ watch again
          </button>
        )}
      </div>

      {/* One shot needs no dots and no replay control — there is nothing to
          navigate, and an 8-second episode plans exactly one. Offering
          "watch again" for a single motionless picture reads as a bug. */}
      {stills.length > 1 && (
        <div className="film-dots" aria-hidden="true">
          {stills.map((url, i) => (
            <span key={url} className={i === index ? 'on' : ''} />
          ))}
        </div>
      )}

      <p className="legend">
        A preview of the scenes we’ll film. The finished version moves — this is
        to show you the characters and places first.
      </p>
    </div>
  );
}
